import type { PdfEngine } from "../types/engine/PdfEngine"
import type { TaskResult } from "../types/tasks/input/TaskResult"
import type { TaskSeparateOptions } from "../types/tasks/options/TaskSeparateOptions"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"
import { PDFSeparation } from "@pdfking/renderer-default"
import { First, Range } from "@pdfking/tfunc"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { ConvertPixels } from "../geometry/ConvertPixels"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TaskName } from "../types/tasks/TaskName"
import { TrackedTaskName } from "../types/tasks/tracking/TrackedTaskName"

const singlePage = (page_count: number) => {
  if (page_count !== 1) {
    throw new Error(`Expected 1 page, got ${page_count}`)
  }
  return Range(1, 1)
}

export async function* TaskSeparate(
  {
    options: { separation_angles, separation_dpi, separation_lpi, unit_to_px },
    source,
  }: TaskSeparateOptions,
  emit: TrackingCallback,
  engine: PdfEngine
): AsyncGenerator<TaskResult> {
  void emit({
    done: 0,
    name: TrackedTaskName.RASTERIZE_PDF,
    total: 1,
  })
  const { size, stream } = await RequestBlob(engine, source, emit)
  const input = { size, stream, type: ContentType.PDF }

  const sep = await First(
    PDFSeparation(
      input,
      { angle: separation_angles, dpi: separation_dpi, lpi: separation_lpi },
      singlePage
    )
  )

  for (const { channel, blob } of sep.channels) {
    const content = await GenerateFile(engine, [blob], ContentType.TIFF, emit)
    yield {
      content: {
        enum: VirtualFileType.SINGLE_PAGE_IMAGE,
        channel,
        content,
        dpi: separation_dpi,
        lpi: separation_lpi,
        size: ConvertPixels(
          { unit_to_px },
          { height: sep.height, width: sep.width }
        ),
        source,
        file_meta: {},
      },
      enum: TaskName.CONTENT_FILE,
      name: `${channel}.tiff`,
    } as TaskResult
  }

  void emit({
    done: 1,
    name: TrackedTaskName.RASTERIZE_PDF,
    total: 1,
  })
}
